import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
  Checkbox,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useArchiveConversation } from '@/api/endpoints/conversation/useArchiveConversation';
import {
  useGetInboxConversations,
  UnifiedInboxConversation,
} from '@/api/endpoints/conversation/useGetInboxConversations';
import { useSearchInboxConversations } from '@/api/endpoints/conversation/useSearchInboxConversations';
import { ClipboardAttachmentIcon } from '@/assets/icons/ClipboardAttachmentIcon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import { Send01Icon } from '@/assets/icons/Send01Icon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatFullDateTime } from '@/utils/date';

interface Contact {
  id: number;
  name: string;
  role: string;
  user_id?: string;
  created_at: string;
  metadata?: unknown;
  details?: Array<{
    id: number;
    type: string;
    value: string;
    label?: string;
    is_primary: boolean;
  }>;
  associations?: Array<{
    id: number;
    building?: { name: string };
    unit?: { name: string };
  }>;
}

interface ContactConversationsTabProps {
  contact: Contact;
}

interface ConversationCardProps {
  conversation: UnifiedInboxConversation;
  selectedRows: string[];
  handleSelectRow: (conversationId: string) => void;
  handleEmailClick: (conversationId: string) => void;
  handleDeleteEmail: (event: React.MouseEvent<HTMLElement>, conversationId: string) => void;
  getInitials: (name: string) => string;
  parseContact: (contact: string) => { name: string; email: string };
  getStatusBgColor: (status: string) => string;
  getStatusColor: (status: string) => string;
  formatContactRole: (role?: string) => string;
  getRoleColor: (role?: string) => string;
}

// Mobile card component moved outside to avoid re-creation on each render
const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  selectedRows,
  handleSelectRow,
  handleEmailClick,
  handleDeleteEmail,
  getInitials,
  parseContact,
  getStatusBgColor,
  getStatusColor,
  formatContactRole,
  getRoleColor,
}) => {
  const isReceived = conversation.type === 'received';
  const isSent = conversation.type === 'sent';

  // Handle contact parsing based on conversation type
  let contactName = '';
  let contactEmail = '';
  let isRead = true;

  if (isReceived) {
    const { name, email } = parseContact(conversation.from);
    contactName = name;
    contactEmail = email;
    isRead = conversation.isRead;
  } else if (isSent) {
    const { name, email } = parseContact(conversation.to);
    contactName = name;
    contactEmail = email;
  }

  // Determine status chip content and styling
  const getStatusChipProps = () => {
    if (isSent) {
      return {
        label: conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1),
        backgroundColor: getStatusBgColor(conversation.status),
        color: getStatusColor(conversation.status),
      };
    }

    return {
      label: isRead ? 'Read' : 'Unread',
      backgroundColor: isRead ? '#e8f5e8' : '#fef7e0',
      color: isRead ? '#34a853' : '#f9ab00',
    };
  };

  const statusChipProps = getStatusChipProps();

  return (
    <Card
      sx={{
        mb: 1,
        backgroundColor: isReceived && !isRead ? '#f6f8fc' : '#ffffff',
        border: selectedRows.includes(conversation.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      }}
      onClick={() => handleEmailClick(conversation.id)}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header with avatar, name, and actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Checkbox
            checked={selectedRows.includes(conversation.id)}
            onChange={() => handleSelectRow(conversation.id)}
            onClick={e => e.stopPropagation()}
            size="small"
          />
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: '0.875rem',
              backgroundColor: isReceived ? '#1a73e8' : '#34a853',
              color: 'white',
            }}
          >
            {getInitials(contactName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {isReceived ? (
                <Mail01Icon sx={{ fontSize: 14, color: '#1a73e8' }} />
              ) : (
                <Send01Icon sx={{ fontSize: 14, color: '#34a853' }} />
              )}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: isReceived && !isRead ? 600 : 500,
                  color: '#202124',
                  fontSize: '0.875rem',
                }}
              >
                {contactName}
              </Typography>
            </Box>
            {contactEmail && (
              <Typography
                variant="caption"
                sx={{
                  color: '#5f6368',
                  fontSize: '0.75rem',
                  display: 'block',
                }}
              >
                {contactEmail}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={event => handleDeleteEmail(event, conversation.id)}
            sx={{
              color: '#5f6368',
              '&:hover': {
                color: '#ea4335',
              },
            }}
          >
            <Trash01Icon fontSize="small" />
          </IconButton>
        </Box>

        {/* Subject and preview */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: isReceived && !isRead ? 600 : 400,
                color: '#202124',
                fontSize: '0.875rem',
                flex: 1,
              }}
            >
              {conversation.lastSubject || conversation.title}
            </Typography>
            {conversation.attachmentCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: '#f1f3f4',
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.25,
                }}
              >
                <ClipboardAttachmentIcon
                  sx={{
                    fontSize: 12,
                    color: '#5f6368',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#5f6368',
                    fontWeight: 500,
                  }}
                >
                  {conversation.attachmentCount}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: '#5f6368',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {conversation.preview}
          </Typography>
        </Box>

        {/* Chips and metadata */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {/* Status chip */}
          <Chip
            label={statusChipProps.label}
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: 24,
              backgroundColor: statusChipProps.backgroundColor,
              color: statusChipProps.color,
              border: 'none',
              fontWeight: 500,
            }}
          />

          {/* Role chip */}
          {conversation.contactRole && (
            <Chip
              label={formatContactRole(conversation.contactRole)}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: 'transparent',
                borderColor: getRoleColor(conversation.contactRole),
                color: getRoleColor(conversation.contactRole),
                fontWeight: 500,
              }}
            />
          )}

          {/* Source chip */}
          <Chip
            label={conversation.source}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: 24,
              backgroundColor: 'transparent',
              borderColor: '#dadce0',
              color: '#5f6368',
            }}
          />

          {/* Date */}
          <Typography
            variant="caption"
            color="#5f6368"
            sx={{
              fontSize: '0.75rem',
              ml: 'auto',
            }}
          >
            {formatFullDateTime(new Date(conversation.timestamp))}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const ContactConversationsTab: React.FC<ContactConversationsTabProps> = ({ contact }) => {
  const { selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>('');
  const [activeFilters, setActiveFilters] = useState<{
    unread: boolean;
    sent: boolean;
  }>({
    unread: false,
    sent: false,
  });

  // Get contact email addresses for filtering
  const contactEmails = useMemo(() => {
    if (!contact.details) return [];
    return contact.details
      .filter(detail => detail.type === 'email')
      .map(detail => detail.value.toLowerCase());
  }, [contact.details]);

  // Determine whether to use search or regular query
  const shouldUseSearch = activeSearchQuery.trim().length > 0;

  const {
    data: regularConversations = [],
    isLoading: isLoadingRegular,
    error: regularError,
  } = useGetInboxConversations(
    {
      location_id: selectedPropertyLocation?.id,
    },
    !shouldUseSearch
  );

  const {
    data: searchConversations = [],
    isLoading: isLoadingSearch,
    error: searchError,
  } = useSearchInboxConversations(
    {
      location_id: selectedPropertyLocation?.id,
      q: activeSearchQuery,
    },
    shouldUseSearch
  );

  // Use the appropriate data based on whether we're searching or not
  const allConversations = shouldUseSearch ? searchConversations : regularConversations;
  const isLoading = shouldUseSearch ? isLoadingSearch : isLoadingRegular;
  const error = shouldUseSearch ? searchError : regularError;

  const parseContact = (contactString: string) => {
    // Parse contact string to extract name and email
    // Handle formats like "John Doe <john@example.com>" or just "john@example.com"
    const emailRegex = /<([^>]+)>/;
    const emailMatch = contactString.match(emailRegex);

    if (emailMatch) {
      const email = emailMatch[1];
      const name = contactString.replace(emailRegex, '').trim();
      return { name: name || email, email };
    }

    // If it's just an email address
    if (contactString.includes('@')) {
      return { name: contactString.split('@')[0], email: contactString };
    }

    // If it's just a name
    return { name: contactString, email: '' };
  };

  // Filter conversations for this specific contact
  const conversations = useMemo(() => {
    if (!allConversations || contactEmails.length === 0) return [];

    return allConversations.filter(conversation => {
      let participantEmail = '';

      // Handle different conversation types with proper type checking
      if (conversation.type === 'received') {
        const { email } = parseContact(conversation.from);
        participantEmail = email;
      } else if (conversation.type === 'sent') {
        const { email } = parseContact(conversation.to);
        participantEmail = email;
      }

      return contactEmails.some(contactEmail =>
        participantEmail.toLowerCase().includes(contactEmail)
      );
    });
  }, [allConversations, contactEmails]);

  const archiveConversationMutation = useArchiveConversation();

  // Calculate filtered conversations and related data
  const sentConversations = conversations.filter(conv => conv.type === 'sent');
  const unreadConversations = conversations.filter(
    conv => conv.type === 'received' && 'isRead' in conv && !conv.isRead
  );

  // Apply filters to conversations
  const filteredConversations = conversations.filter(conversation => {
    // If no filters are active, show all conversations
    if (!activeFilters.unread && !activeFilters.sent) {
      return true;
    }

    // If both filters are active, show conversations that match either filter
    if (activeFilters.unread && activeFilters.sent) {
      const isUnread = conversation.type === 'received' && !conversation.isRead;
      const isSent = conversation.type === 'sent';
      return isUnread || isSent;
    }

    // If only unread filter is active
    if (activeFilters.unread) {
      return conversation.type === 'received' && !conversation.isRead;
    }

    // If only sent filter is active
    if (activeFilters.sent) {
      return conversation.type === 'sent';
    }

    return true;
  });

  const handleFilterToggle = (filterType: 'unread' | 'sent') => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const handleEmailClick = (conversationId: string) => {
    history.push(`${PMPRoutes.inbox}/${conversationId}`);
  };

  const handleDeleteEmail = (event: React.MouseEvent<HTMLElement>, conversationId: string) => {
    event.stopPropagation();
    // Archive the conversation instead of deleting it
    archiveConversationMutation.mutate({
      conversationSid: conversationId,
      locationId: selectedPropertyLocation?.id,
    });
  };

  const handleSelectRow = (conversationId: string) => {
    if (selectedRows.includes(conversationId)) {
      setSelectedRows(selectedRows.filter(id => id !== conversationId));
    } else {
      setSelectedRows([...selectedRows, conversationId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredConversations.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredConversations.map(conv => conv.id));
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const getStatusColor = (status: string) => {
    switch (status) {
    case 'sent':
      return '#f9ab00';
    case 'delivered':
      return '#34a853';
    case 'failed':
      return '#ea4335';
    default:
      return '#5f6368';
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
    case 'sent':
      return '#fef7e0';
    case 'delivered':
      return '#e8f5e8';
    case 'failed':
      return '#fce8e6';
    default:
      return '#f1f3f4';
    }
  };

  const formatContactRole = (role?: string) => {
    if (!role) return '';

    // Convert from ENUM_CASE to Title Case
    return role
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getRoleColor = (role?: string) => {
    if (!role) return '#5f6368';

    switch (role) {
    case 'TENANT':
      return '#1a73e8';
    case 'LANDLORD':
      return '#ea4335';
    case 'PROPERTY_MANAGER':
      return '#34a853';
    case 'OWNER_REPRESENTATIVE':
      return '#f9ab00';
    default:
      return '#5f6368';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography variant="body2" color="error">
          Failed to load conversations. Please try again.
        </Typography>
      </Box>
    );
  }

  // Render empty state
  const renderEmptyState = () => {
    const hasActiveSearch = shouldUseSearch || activeFilters.unread || activeFilters.sent;
    const hasAnyConversations = conversations.length > 0;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
          px: 2,
        }}
      >
        <Mail01Icon sx={{ fontSize: 48, color: '#dadce0', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {hasActiveSearch ? 'No matching conversations' : 'No conversations found'}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {hasActiveSearch && hasAnyConversations
            ? 'Try adjusting your search or filters to find conversations.'
            : `No conversations with ${contact.name} yet.`}
        </Typography>
        {hasActiveSearch && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              handleClearSearch();
              setActiveFilters({ unread: false, sent: false });
            }}
            sx={{ mt: 2 }}
          >
            Clear search and filters
          </Button>
        )}
      </Box>
    );
  };

  // Render mobile layout
  const renderMobileLayout = () => (
    <Box sx={{ p: 1 }}>
      {filteredConversations.map(conversation => (
        <ConversationCard
          key={conversation.id}
          conversation={conversation}
          selectedRows={selectedRows}
          handleSelectRow={handleSelectRow}
          handleEmailClick={handleEmailClick}
          handleDeleteEmail={handleDeleteEmail}
          getInitials={getInitials}
          parseContact={parseContact}
          getStatusBgColor={getStatusBgColor}
          getStatusColor={getStatusColor}
          formatContactRole={formatContactRole}
          getRoleColor={getRoleColor}
        />
      ))}
    </Box>
  );

  // Render desktop table row
  const renderTableRow = (conversation: UnifiedInboxConversation) => {
    const isReceived = conversation.type === 'received';
    const isSent = conversation.type === 'sent';

    // Handle contact parsing based on conversation type
    let contactName = '';
    let contactEmail = '';
    let isRead = true;

    if (isReceived) {
      const { name, email } = parseContact(conversation.from);
      contactName = name;
      contactEmail = email;
      isRead = conversation.isRead;
    } else if (isSent) {
      const { name, email } = parseContact(conversation.to);
      contactName = name;
      contactEmail = email;
    }

    // Get status chip props
    const getStatusChipProps = () => {
      if (isSent) {
        return {
          label: conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1),
          backgroundColor: getStatusBgColor(conversation.status),
          color: getStatusColor(conversation.status),
        };
      }

      return {
        label: isRead ? 'Read' : 'Unread',
        backgroundColor: isRead ? '#e8f5e8' : '#fef7e0',
        color: isRead ? '#34a853' : '#f9ab00',
      };
    };

    const statusChipProps = getStatusChipProps();

    return (
      <TableRow
        key={conversation.id}
        hover
        selected={selectedRows.includes(conversation.id)}
        onClick={() => handleEmailClick(conversation.id)}
        sx={{
          cursor: 'pointer',
          backgroundColor: isReceived && !isRead ? '#f6f8fc' : '#ffffff',
          '&:hover': {
            backgroundColor: '#f5f5f5 !important',
          },
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #f0f0f0',
            py: 1.5,
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={selectedRows.includes(conversation.id)}
            onChange={() => handleSelectRow(conversation.id)}
            onClick={e => e.stopPropagation()}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                backgroundColor: isReceived ? '#1a73e8' : '#34a853',
                color: 'white',
              }}
            >
              {getInitials(contactName)}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isReceived ? (
                  <Mail01Icon sx={{ fontSize: 12, color: '#1a73e8' }} />
                ) : (
                  <Send01Icon sx={{ fontSize: 12, color: '#34a853' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isReceived && !isRead ? 600 : 400,
                    color: '#202124',
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }}
                >
                  {contactName}
                </Typography>
              </Box>
              {contactEmail && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#5f6368',
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                  }}
                >
                  {contactEmail}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isReceived && !isRead ? 600 : 400,
                  color: '#202124',
                  fontSize: '0.875rem',
                }}
              >
                {conversation.lastSubject || conversation.title}
              </Typography>
              {conversation.attachmentCount > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: '#f1f3f4',
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                  }}
                >
                  <ClipboardAttachmentIcon
                    sx={{
                      fontSize: 12,
                      color: '#5f6368',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: '#5f6368',
                      fontWeight: 500,
                    }}
                  >
                    {conversation.attachmentCount}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: '#5f6368',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '400px',
              }}
            >
              {conversation.preview}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={statusChipProps.label}
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: 24,
              backgroundColor: statusChipProps.backgroundColor,
              color: statusChipProps.color,
              border: 'none',
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell>
          {conversation.contactRole ? (
            <Chip
              label={formatContactRole(conversation.contactRole)}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: 'transparent',
                borderColor: getRoleColor(conversation.contactRole),
                color: getRoleColor(conversation.contactRole),
                fontWeight: 500,
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={conversation.source}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: 24,
              backgroundColor: 'transparent',
              borderColor: '#dadce0',
              color: '#5f6368',
            }}
          />
        </TableCell>
        <TableCell>
          <Typography
            variant="caption"
            color="#5f6368"
            sx={{
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              lineHeight: 1.2,
            }}
          >
            {formatFullDateTime(new Date(conversation.timestamp))}
          </Typography>
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={event => handleDeleteEmail(event, conversation.id)}
            sx={{
              opacity: 0.5,
              '&:hover': {
                opacity: 1,
                color: '#ea4335',
              },
            }}
          >
            <Trash01Icon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  // Render desktop table layout
  const renderDesktopLayout = () => (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow
            sx={{ '& .MuiTableCell-head': { backgroundColor: '#f8f9fa', borderBottom: 'none' } }}
          >
            <TableCell padding="checkbox" width="40px" />
            <TableCell width="200px">
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Contact
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Subject
              </Typography>
            </TableCell>
            <TableCell width="100px">
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Status
              </Typography>
            </TableCell>
            <TableCell width="120px">
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Role
              </Typography>
            </TableCell>
            <TableCell width="120px">
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Source
              </Typography>
            </TableCell>
            <TableCell width="160px">
              <Typography variant="caption" fontWeight={500} color="#5f6368">
                Date
              </Typography>
            </TableCell>
            <TableCell width="60px" />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredConversations.map(conversation => renderTableRow(conversation))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render main content based on state and screen size
  const renderMainContent = () => {
    if (filteredConversations.length === 0) {
      return renderEmptyState();
    }

    if (isMobile) {
      return renderMobileLayout();
    }

    return renderDesktopLayout();
  };

  return (
    <Box sx={{ height: '100%', backgroundColor: '#ffffff' }}>
      {/* Gmail-style toolbar with search */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {/* Search bar */}
        <Box
          sx={{
            px: isMobile ? 1 : 2,
            py: 1,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search conversations with ${contact.name}...`}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchLgIcon sx={{ color: 'text.disabled', fontSize: '1.125rem' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: '#f8f9fa',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #dadce0',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #1976d2',
                  },
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              color="primary"
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 1,
                borderRadius: 2,
              }}
            >
              Search
            </Button>
            {activeSearchQuery && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearSearch}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  borderColor: '#dadce0',
                  color: '#5f6368',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: isMobile ? 1 : 2,
            py: 1,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 1 : 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, flex: 1 }}>
            <Checkbox
              checked={
                selectedRows.length === filteredConversations.length &&
                filteredConversations.length > 0
              }
              indeterminate={
                selectedRows.length > 0 && selectedRows.length < filteredConversations.length
              }
              onChange={handleSelectAll}
              size="small"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              {isLoading ? (
                'Loading...'
              ) : (
                <>
                  {filteredConversations.length}{' '}
                  {filteredConversations.length === 1 ? 'conversation' : 'conversations'}
                  {shouldUseSearch && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ ml: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      (filtered)
                    </Typography>
                  )}
                  {(activeFilters.unread || activeFilters.sent) && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ ml: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      (filtered)
                    </Typography>
                  )}
                </>
              )}
            </Typography>
            {selectedRows.length > 0 && (
              <IconButton
                size="small"
                onClick={() => {
                  selectedRows.forEach(conversationId => {
                    archiveConversationMutation.mutate({
                      conversationSid: conversationId,
                      locationId: selectedPropertyLocation?.id,
                    });
                  });
                  setSelectedRows([]);
                }}
                sx={{
                  color: '#ea4335',
                  '&:hover': {
                    backgroundColor: 'rgba(234, 67, 53, 0.1)',
                  },
                }}
              >
                <Trash01Icon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <Chip
              icon={<Mail01Icon />}
              label={`${unreadConversations.length} unread`}
              size="small"
              variant={activeFilters.unread ? 'filled' : 'outlined'}
              onClick={() => handleFilterToggle('unread')}
              sx={{
                fontSize: isMobile ? '0.625rem' : '0.75rem',
                backgroundColor: activeFilters.unread ? '#1976d2' : 'transparent',
                borderColor: activeFilters.unread ? '#1976d2' : '#dadce0',
                color: activeFilters.unread ? '#ffffff' : '#5f6368',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: activeFilters.unread ? '#1565c0' : 'rgba(25, 118, 210, 0.04)',
                  borderColor: '#1976d2',
                },
              }}
            />
            <Chip
              icon={<Send01Icon />}
              label={`${sentConversations.length} sent`}
              size="small"
              variant={activeFilters.sent ? 'filled' : 'outlined'}
              onClick={() => handleFilterToggle('sent')}
              sx={{
                fontSize: isMobile ? '0.625rem' : '0.75rem',
                backgroundColor: activeFilters.sent ? '#34a853' : 'transparent',
                borderColor: activeFilters.sent ? '#34a853' : '#dadce0',
                color: activeFilters.sent ? '#ffffff' : '#5f6368',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: activeFilters.sent ? '#2e7d32' : 'rgba(52, 168, 83, 0.04)',
                  borderColor: '#34a853',
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Content area */}
      <Box sx={{ height: 'calc(100% - 120px)', overflow: 'auto' }}>{renderMainContent()}</Box>
    </Box>
  );
};

export default ContactConversationsTab;
